import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { getAdfApps } from './data-sources/adf-tracking-source'
import { getCodatCompanies } from './data-sources/codat-company-source'
import { updateAdfApp as updateAdfAppSink } from './data-sources/adf-app-sink'
import { AdfApplication, AdfApplicationSink, CodatCompany } from './adf-app-types'
import { makeAdfRows, replaceNoneWithNull } from './adf-info-manager'
import { useDispatch } from 'react-redux'
import { setSnackBar } from '../../stores/ui/ui-action'

const AdfsContext = createContext(null)

export default function AdfsContextProvider({children}) {
    const [adfs, setAdfs] = useState<AdfApplication[]>([])
    const [codatCompanies, setCodatCompanies] = useState<CodatCompany[]>([])
    const [adfRowsForGrid, setAdfRowsForGrid] = useState<any[]>([])
    const dispatch = useDispatch()


    // Handles re-rendering the MUI Grid row item state before and after updating any ADF item.
    const updateAdfRowsForGrid = useCallback(() => {
        const formattedRows = makeAdfRows(adfs, codatCompanies)
        setAdfRowsForGrid(formattedRows)
    }, [adfs, codatCompanies])

    useEffect(() => {
        updateAdfRowsForGrid()
    }, [adfs, codatCompanies, updateAdfRowsForGrid])

    
    

    // This group handles getting the ADF and Codat data and updating an individual ADF.
    const callGetAdfApps = useCallback(async () => {
        try {
            const data = await getAdfApps()
            setAdfs(data)
        } catch (error) {
            console.error('Problem getting ADF applications: ', error)
            dispatch(
                setSnackBar({
                    show: true,
                    message: 'There was a problem getting the application data. Please refresh to try again.',
                    messageType: 'error',
                })
            )
        }

        try {
            const data = await getCodatCompanies()
            setCodatCompanies(data)
        } catch (error) {
            console.error('Problem getting Codat companies: ', error)
            dispatch(
                setSnackBar({
                    show: true,
                    message: 'There was a problem getting the application data. Please refresh to try again.',
                    messageType: 'error',
                })
            )
        }

    }, [])

    async function updateAdfApp(selectedAdf: AdfApplication, formData: any): Promise<boolean> {
        const mergedAdfData: AdfApplication = {
            ...selectedAdf,
            ...formData
        }

        const validateUserInfoChange = () => {

            if (formData.userPhoneNumber !== selectedAdf.userPhoneNumber) {

                return {
                    phoneNumber: formData.userPhoneNumber,
                    email: formData.userEmail
                }
            }

            return null

        }

        const payload: AdfApplicationSink = {
            adfId: mergedAdfData.adfId,
            companyId: mergedAdfData.companyId,
            creditSolutionsContact: mergedAdfData.creditSolutionsContact,
            creditSolutionsContactEmail: mergedAdfData.creditSolutionsContactEmail,
            teamCode: mergedAdfData.teamCode,
            cif: mergedAdfData.cif,
            crmEntityId: replaceNoneWithNull(mergedAdfData.crmEntityId),
            userInfo: validateUserInfoChange()
        }

        await updateAdfAppSink(payload)

        setAdfs(prevAdfs => prevAdfs.map(adf => adf.adfId === selectedAdf.adfId ? mergedAdfData : adf))

        return true;
    }

    
    
    return (
        <AdfsContext.Provider value={{
            adfRowsForGrid,
            callGetAdfApps,
            updateAdfApp 
        }}>
            {children}
        </AdfsContext.Provider>
    )
}


// Custom hook acts has an API/boundary to the Context.
export function useAdfsContext() {
    return useContext(AdfsContext);
}